<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import consentService from '@services/consent-service'
import { isIosBrowser } from '@utils/user-agent'

const emit = defineEmits(['close'])

const isVisible = ref(true)
const showAboveBanner = ref(false)
const banner = ref(null)

function handleAgree() {
    consentService.grantConsent('banner')
    emit('close')
}

function handleTemporaryDismiss() {
    isVisible.value = false
    consentService.temporarilyDismiss()
}

function handleKeydown(event) {
    if (event.key === 'Escape') {
        handleTemporaryDismiss()
    }
}

function handleDocumentClick(event) {
    if (isVisible.value && banner.value && !banner.value.contains(event.target)) {
        handleTemporaryDismiss()
    }
}

onMounted(() => {
    showAboveBanner.value = isIosBrowser()
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('click', handleDocumentClick, true)
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('click', handleDocumentClick, true)
})
</script>

<template>
    <div
        v-if="isVisible"
        ref="banner"
        :class="['consent-banner', showAboveBanner && 'consent-banner-above-smart-banner']"
        role="dialog"
        aria-modal="false"
        aria-label="Privacy consent"
    >
        <p class="text">
            By using this site, you agree to our
            <a
                class="link"
                href="https://frisky.fm/about/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
            >Privacy Policy</a>.
        </p>
        <BaseButton
            button-type="secondaryPink"
            size="small"
            :modifiers="{ noMargins: true }"
            @click="handleAgree"
        >
            I agree
        </BaseButton>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.consent-banner {
    position: fixed;
    bottom: $s-m;
    left: 50%;
    z-index: $layer-modal-z-index + 2;
    display: flex;
    gap: $s-l;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - #{$s-m} * 2);
    padding: $s-l;
    border-radius: $size-input-border-radius;
    transform: translateX(-50%);

    @include elevationOverlay;
}

.text {
    @extend %contentSubtitleM;

    margin: 0;
    color: $c-white;
}

.link {
    color: $c-white;
    text-decoration: underline;

    &:hover {
        color: $c-highlight;
    }
}

.consent-banner-above-smart-banner {
    bottom: calc(#{$s-m} + #{$size-smart-banner-height} + env(safe-area-inset-bottom, 0));
}

@include desktop {
    .consent-banner {
        right: $s-xl;
        bottom: $s-xl;
        left: auto;
        width: auto;
        max-width: 480px;
        transform: none;
    }
}
</style>
